import { DateTime } from "luxon";
import { TCasesDocumentType } from "../../models/CaseDocumentType1";
import { CaseLenderDictionary } from "../../codecs/CasesLenderNameOnPanel";
import { TCaseLedgerType } from "../../codecs/CaseLedger";
import { TRoadblockCaseData, TRoadblockCheckListItem, TRoadblockClient } from "../roadblockTypesAndCodecs";
import { TCasePropertyForRefreshRoadblocksData } from "../../codecs/CasePropertyForRefreshRoadblocksData";
import { allDocumentSlotsReceived, anyDocSlotExists } from "./ProbateItems";
import { checklistItemResolvedByParty } from "../checklistItemResolvedByParty";
import { clientName, clientsOfTypes, fact, formatEvidenceDate, neutralFact } from "../evidence/evidenceHelpers";
import { TRoadblockEvidenceFact, TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";
import { formatCurrencyFromPence } from "../../../shared/src/util/numberFormatting";
import { CanRequireSourceOfWealthCheck, RequiresBankruptcySearchPurchaseTransferOrRemortgage, RequiresBankruptcySearchSale, UserCaseUserType_UserCaseUserTypeDisplayName } from "../../codecs/UserCaseUserType";

const mortgageOfferLink: TRoadblockEvidenceLink = { block: "case_details", section: "mortgage_offer" };
const sdltLink: TRoadblockEvidenceLink = { block: "case_details", section: "stamp_duty_land_tax" };
const chargesLink: TRoadblockEvidenceLink = { block: "charges_and_restrictions", section: null };
const bankAccountsLink: TRoadblockEvidenceLink = { block: "case_details", section: "client_bank_accounts" };
const riskLink: TRoadblockEvidenceLink = { block: "case_details", section: "risk_management" };
const clientFundsLink: TRoadblockEvidenceLink = { block: "case_details", section: "client_funds" };
const titleChecksLink: TRoadblockEvidenceLink = { block: "case_title_checks", section: null };
const dualRepLink: TRoadblockEvidenceLink = { block: "case_details", section: "dual_rep" };
const managementCompanyLink: TRoadblockEvidenceLink = { block: "case_details", section: "management_company_details" };
const clientsLink: TRoadblockEvidenceLink = { block: "case_details", section: "clients" };
const surveyLink: TRoadblockEvidenceLink = { block: "case_details", section: "survey" };

const fundersNeedingSourceOfWealth = (c: TRoadblockCaseData) =>
    clientsOfTypes(c.clients, CanRequireSourceOfWealthCheck)
        .filter((client) => client.needs_source_of_wealth_check);

const propertyLabel = (p: TCasePropertyForRefreshRoadblocksData, index: number): string =>
    p.postcode !== null && p.postcode.trim() !== "" ? p.postcode : `Title ${index + 1}`;

const ledgerEntryPaidFact = (
    c: TRoadblockCaseData,
    type: TCaseLedgerType,
    label: string,
): TRoadblockEvidenceFact => {
    const exists = c.case_ledger_entries.some((l) => l.type === type && l.deleted_at === null);
    return fact(label, exists ? "Added to ledger" : "Not added", exists, clientFundsLink);
};

const receivedDocFact = (
    c: TRoadblockCaseData,
    types: Array<TCasesDocumentType>,
    label: string,
    link: TRoadblockEvidenceLink | null = null,
): TRoadblockEvidenceFact => {
    const received = anyReceivedDocumentExists(c, types);
    return fact(label, received ? "Received" : "Not received", received, link);
};

const docSlotsReceivedFact = (
    c: TRoadblockCaseData,
    types: Array<TCasesDocumentType>,
    label: string,
): TRoadblockEvidenceFact => {
    const slots = c.cases_documents.filter(({ type }) => types.includes(type));
    const received = slots.filter(({ received_date }) => received_date !== null).length;
    const allReceived = slots.length > 0 && received === slots.length;
    return fact(label, `${received} of ${slots.length} received`, allReceived);
};

export const anyReceivedDocumentExists = (c: TRoadblockCaseData, types: Array<TCasesDocumentType>) =>
    c.cases_documents.filter(
        ({ type, received_date }) =>
            types.includes(type)
            && received_date !== null
    ).length > 0;

export const anyActiveLedgerEntryOfType = (c: TRoadblockCaseData, type: TCaseLedgerType) =>
    c.case_ledger_entries.filter((l) => l.type === type && l.deleted_at === null).length > 0;

export const anyLeaseholdProperty = (c: TRoadblockCaseData) =>
    c.cases_properties.filter((p) => p.tenure === "leasehold").length > 0;

const clientsNeedingBankruptcySearch = (c: TRoadblockCaseData) =>
    mortgageInvolved(c)
        ? clientsOfTypes(c.clients, RequiresBankruptcySearchPurchaseTransferOrRemortgage)
    : c.case.transaction_type === "sale"
        ? clientsOfTypes(c.clients, RequiresBankruptcySearchSale)
    : [];

const bankruptcySearchSatisfied = (client: TRoadblockClient, c: TRoadblockCaseData) =>
    client.bankruptcy_search_result !== "unknown"
    && (
        !mortgageInvolved(c)
        || client.bankruptcy_search_result === "not_applicable"
        || (
            client.bankruptcy_search_expiry_date !== null
            && DateTime.fromISO(client.bankruptcy_search_expiry_date) > DateTime.local()
        )
    );

export const mortgageInvolved = (c: TRoadblockCaseData) =>
    c.case.transaction_type === "remortgage"
    || (
        (c.case.transaction_type === "purchase" || c.case.transaction_type === "transfer")
        && c.case.purchase_payment_method === "mortgage"
    );

export const allMortgageOfferQuestionsAnswered = (c: TRoadblockCaseData) =>
    c.case.mortgage_offer_date !== null
    && c.case.mortgage_offer_advance_amount_pence !== null
    && c.case.mortgage_offer_retention_amount_pence !== null
    && c.case.mortgage_offer_term_months !== null
    && c.case.mortgage_offer_interest_rate !== null
    && c.case.mortgage_offer_early_redemption_penalty !== "unknown"
    && c.case.mortgage_offer_all_client_names_correct
    && c.case.mortgage_offer_address_correct
    && c.case.mortgage_offer_amount_matches_purchase_price
    && c.case.mortgage_offer_tenure_matches_contract_tenure
    && c.case.mortgage_offer_lender_requirements_checked_and_case_can_proceed
    && c.case.mortgage_offer_we_require_buildings_insurance !== null
    && c.case.mortgage_offer_an_occupier_consent_form_is_required !== null
    && c.case.mortgage_offer_lender_requires_a_new_mortgage_deed !== null
    && c.case.mortgage_offer_there_are_matters_we_need_to_refer !== null
    && (
        c.case.mortgage_offer_there_are_matters_we_need_to_refer !== true
        || c.case.mortgage_offer_matters_to_refer_details !== ""
    );

export const mortgageOfferDocValid = (c: TRoadblockCaseData) =>
    c.cases_documents.filter((d) =>
        d.type === "mortgage_offer"
        && d.received_date !== null
    ).length > 0;

export const allClientsHaveSdltDeclared = (c: TRoadblockCaseData) => {
    const clientIds = c.clients.filter((u) => u.user_type === "client").map((u) => u.id);
    const declaredIds = c.sdlt_declarations.map((d) => d.user_id);
    return clientIds.length > 0 && clientIds.every((id) => declaredIds.includes(id));
};

export const recentLastTransferRiskAssessed = (c: TRoadblockCaseData) =>
    c.cases_properties.every(({ is_registered, last_transfer_date, recent_last_transfer_date_risk_acknowledged }) =>
        is_registered === false
        || (
            is_registered === true
            && last_transfer_date !== null
            && (
                DateTime.fromISO(last_transfer_date).toSeconds() < DateTime.utc().minus({ months: 6 }).toSeconds()
                || recent_last_transfer_date_risk_acknowledged === true
            )
        )
    );

export const allBankAccountsCleared = (c: TRoadblockCaseData) =>
    c.bank_accounts.every((b) =>
        b.verification_status === "not_verified_as_will_not_be_used"
        || (b.verification_status === "approved" && b.all_clients_agree_with_this_bank_account !== null)
    );

export const sdltNotRequired = (c: TRoadblockCaseData) =>
    c.case.sdlt_answers_reviewed_outcome === "not_required";

export const sdltApplies = (c: TRoadblockCaseData) =>
    (c.case.transaction_type === "purchase" || c.case.transaction_type === "transfer")
    && c.case.price_pence !== null
    && c.case.price_pence >= 4000000;

export const sharedExchangeAndPreCompletionItems = (): Array<TRoadblockCheckListItem> => [
    {
        type: "undertakings_approved_or_marked_none",
        is_resolved_by_short_description: "Non-standard undertakings approved (or marked as none)",
        create: { create_condition_description: "All cases", predicate: () => true },
        resolve: {
            is_resolved_by_long_description: `Either marking there are no non-standard undertakings, or marking the non-standard undertakings as approved.`,
            predicate: (c) =>
                c.case.has_non_standard_undertakings === false
                || (c.case.has_non_standard_undertakings === true && c.case.non_standard_undertakings_approved !== null),
            evidence: (c) => {
                if (c.case.has_non_standard_undertakings === false) {
                    return [fact("Non-standard undertakings", "None", true)];
                }
                if (c.case.has_non_standard_undertakings === null) {
                    return [fact("Non-standard undertakings", "Not answered", false)];
                }
                const approved = c.case.non_standard_undertakings_approved;
                return [
                    fact(
                        "Non-standard undertakings",
                        approved !== null ? `Approved ${formatEvidenceDate(approved)}` : "Not approved",
                        approved !== null,
                    ),
                ];
            },
        },
    },

    {
        type: "risk_grade_green_and_recent",
        is_resolved_by_short_description: "Risk grade green and recent",
        create: { create_condition_description: "All cases", predicate: () => true },
        resolve: {
            is_resolved_by_long_description: `The most recent risk assessment being green.`,
            predicate: (c) => c.latest_risk_assessment !== null && c.latest_risk_assessment.grade === "green",
            evidence: (c) => {
                const ra = c.latest_risk_assessment;
                if (ra === null) {
                    return [fact("Risk grade", "No assessment", false, riskLink)];
                }
                return [
                    fact(
                        "Risk grade",
                        `${ra.grade} (assessed ${formatEvidenceDate(ra.created_at)})`,
                        ra.grade === "green",
                        riskLink,
                    ),
                ];
            },
        },
    },
    {
        type: "recent_last_transfer_risk_assessed",
        is_resolved_by_short_description: "Recent-last-transfer risk acknowledged for every title",
        create: { create_condition_description: "All cases", predicate: () => true },
        resolve: {
            is_resolved_by_long_description: `For every registered title, either the last transfer was more than 6 months ago or the recent-last-transfer risk has been acknowledged.`,
            predicate: (c) => recentLastTransferRiskAssessed(c),
            evidence: (c) => c.cases_properties.map((p, index) => {
                if (p.is_registered === false) {
                    return neutralFact(propertyLabel(p, index), "Unregistered", titleChecksLink);
                }
                if (p.is_registered !== true || p.last_transfer_date === null) {
                    return fact(propertyLabel(p, index), "Last transfer date unknown", false, titleChecksLink);
                }
                const isOldTransfer =
                    DateTime.fromISO(p.last_transfer_date).toSeconds() < DateTime.utc().minus({ months: 6 }).toSeconds();
                const resolved = isOldTransfer || p.recent_last_transfer_date_risk_acknowledged === true;
                const value = isOldTransfer
                    ? `Last transfer ${formatEvidenceDate(p.last_transfer_date)}`
                    : p.recent_last_transfer_date_risk_acknowledged === true
                    ? `Recent transfer ${formatEvidenceDate(p.last_transfer_date)} — risk acknowledged`
                    : `Recent transfer ${formatEvidenceDate(p.last_transfer_date)} — risk not acknowledged`;
                return fact(propertyLabel(p, index), value, resolved, titleChecksLink);
            }),
        },
    },
    {
        type: "all_charges_and_title_restrictions_marked_added",
        is_resolved_by_short_description: "All charges and title restrictions marked as added",
        create: {
            create_condition_description: "Sale / transfer / remortgage cases (purchase doesn't need this)",
            predicate: (c) => c.case.transaction_type !== "purchase",
        },
        resolve: {
            is_resolved_by_long_description: `Marking the case's add-charges-and-title-restrictions job as done.`,
            predicate: (c) => c.case.add_charges_and_title_restrictions_job_done_at !== null,
            evidence: (c) => {
                const doneAt = c.case.add_charges_and_title_restrictions_job_done_at;
                return [
                    fact(
                        "Charges & restrictions",
                        doneAt !== null ? `Marked added ${formatEvidenceDate(doneAt)}` : "Not marked added",
                        doneAt !== null,
                        chargesLink,
                    ),
                ];
            },
        },
    },

    {
        type: "search_report_uploaded",
        is_resolved_by_short_description: "Search report received",
        create: {
            create_condition_description: "Non-sale cases with searches ordered",
            predicate: (c) =>
                c.case.searches_ordered !== null
                && c.case.transaction_type !== "sale",
        },
        resolve: {
            is_resolved_by_long_description: `A search_report document being uploaded and marked as received.`,
            predicate: (c) => anyReceivedDocumentExists(c, ["search_report"]),
            evidence: (c) => [receivedDocFact(c, ["search_report"], "Search report")],
        },
    },
    {
        type: "leasehold_report_uploaded",
        is_resolved_by_short_description: "Leasehold report received",
        create: {
            create_condition_description: "Purchase cases with a leasehold property",
            predicate: (c) => c.case.transaction_type === "purchase" && anyLeaseholdProperty(c),
        },
        resolve: {
            is_resolved_by_long_description: `A leasehold_pack_report document being uploaded and marked as received.`,
            predicate: (c) => anyReceivedDocumentExists(c, ["leasehold_pack_report"]),
            evidence: (c) => [receivedDocFact(c, ["leasehold_pack_report"], "Leasehold report")],
        },
    },
    {
        type: "mortgage_report_uploaded",
        is_resolved_by_short_description: "Mortgage offer report received",
        create: {
            create_condition_description: "Cases involving a mortgage",
            predicate: (c) => mortgageInvolved(c),
        },
        resolve: {
            is_resolved_by_long_description: `A mortgage_offer_report document being uploaded and marked as received.`,
            predicate: (c) => anyReceivedDocumentExists(c, ["mortgage_offer_report"]),
            evidence: (c) => [receivedDocFact(c, ["mortgage_offer_report"], "Mortgage offer report", mortgageOfferLink)],
        },
    },
    {
        type: "title_report_uploaded",
        is_resolved_by_short_description: "Title report received",
        create: {
            create_condition_description: "Cases with a title report document slot",
            predicate: (c) => anyDocSlotExists(c, ["title_report"]),
        },
        resolve: {
            is_resolved_by_long_description: `A title_report document being uploaded and marked as received.`,
            predicate: (c) => anyReceivedDocumentExists(c, ["title_report"]),
            evidence: (c) => [receivedDocFact(c, ["title_report"], "Title report")],
        },
    },
    {
        type: "survey_status_answered",
        is_resolved_by_short_description: "Survey position established",
        create: {
            create_condition_description: "Purchase cases",
            predicate: (c) => c.case.transaction_type === "purchase",
        },
        resolve: {
            is_resolved_by_long_description: `Recording whether the client has requested a survey — any answer other than "unknown".`,
            predicate: (c) => c.case.survey_requested !== "unknown",
            evidence: (c) => [
                fact(
                    "Survey requested",
                    c.case.survey_requested === "unknown" ? "Not established" : c.case.survey_requested,
                    c.case.survey_requested !== "unknown",
                    surveyLink,
                ),
            ],
        },
    },
    {
        type: "bankruptcy_search_complete",
        is_resolved_by_short_description: "Bankruptcy searches complete for all clients",
        create: {
            create_condition_description: "Mortgage-involved cases, or sale cases with a deceased owner",
            predicate: (c) => clientsNeedingBankruptcySearch(c).length > 0,
        },
        resolve: {
            is_resolved_by_long_description: `Every client who needs one having a bankruptcy search result. On mortgage-involved cases the result must also be unexpired, unless it is "not applicable".`,
            predicate: (c) => clientsNeedingBankruptcySearch(c).every((client) => bankruptcySearchSatisfied(client, c)),
            evidence: (c) => clientsNeedingBankruptcySearch(c).map((client) => fact(
                clientName(client),
                client.bankruptcy_search_result === "unknown" ? "No search result"
                : client.bankruptcy_search_result === "not_applicable" ? "Not applicable"
                : bankruptcySearchSatisfied(client, c) ? `${client.bankruptcy_search_result} (valid to ${formatEvidenceDate(client.bankruptcy_search_expiry_date ?? "")})`
                : `${client.bankruptcy_search_result} — expired`,
                bankruptcySearchSatisfied(client, c),
                clientsLink,
            )),
        },
    },

    {
        type: "mortgage_lender_identified",
        is_resolved_by_short_description: "Mortgage lender identified",
        create: {
            create_condition_description: "Cases involving a mortgage",
            predicate: (c) => mortgageInvolved(c),
        },
        resolve: {
            is_resolved_by_long_description: `Setting the mortgage_lender_name on the case (anything other than "unknown").`,
            predicate: (c) => c.case.mortgage_lender_name !== "unknown",
            evidence: (c) => [
                fact(
                    "Mortgage lender",
                    c.case.mortgage_lender_name === "unknown" ? "Not identified" : c.case.mortgage_lender_name,
                    c.case.mortgage_lender_name !== "unknown",
                    mortgageOfferLink,
                ),
            ],
        },
    },
    {
        type: "mortgage_offer_added",
        is_resolved_by_short_description: "Mortgage offer added",
        create: {
            create_condition_description: "Cases involving a mortgage",
            predicate: (c) => mortgageInvolved(c),
        },
        resolve: {
            is_resolved_by_long_description: `Recording the mortgage offer date on the case.`,
            predicate: (c) => c.case.mortgage_offer_date !== null,
            evidence: (c) => [
                fact(
                    "Mortgage offer date",
                    c.case.mortgage_offer_date !== null ? formatEvidenceDate(c.case.mortgage_offer_date) : "Not recorded",
                    c.case.mortgage_offer_date !== null,
                    mortgageOfferLink,
                ),
            ],
        },
    },
    {
        type: "mortgage_offer_questions_answered",
        is_resolved_by_short_description: "Mortgage offer review questions answered",
        create: {
            create_condition_description: "Cases involving a mortgage",
            predicate: (c) => mortgageInvolved(c),
        },
        resolve: {
            is_resolved_by_long_description: `Answering every mortgage-offer review question on the case.`,
            predicate: (c) => allMortgageOfferQuestionsAnswered(c),
            evidence: (c) => {
                const answered = allMortgageOfferQuestionsAnswered(c);
                return [
                    fact(
                        "Mortgage offer review questions",
                        answered ? "All answered" : "Not all answered",
                        answered,
                        mortgageOfferLink,
                    ),
                ];
            },
        },
    },
    {
        type: "mortgage_offer_not_expired",
        is_resolved_by_short_description: "Mortgage offer document received (and not expired)",
        create: {
            create_condition_description: "Cases involving a mortgage",
            predicate: (c) => mortgageInvolved(c),
        },
        resolve: {
            is_resolved_by_long_description: `A mortgage_offer document being uploaded, marked as received, and still valid.`,
            predicate: (c) => mortgageOfferDocValid(c),
            evidence: (c) => {
                const valid = mortgageOfferDocValid(c);
                const expiry = c.case.mortgage_offer_expiry_date;
                return [
                    fact(
                        "Mortgage offer document",
                        valid
                            ? expiry !== null ? `Received, expires ${formatEvidenceDate(expiry)}` : "Received"
                            : "Not received",
                        valid,
                        mortgageOfferLink,
                    ),
                ];
            },
        },
    },
    {
        type: "mortgage_retention_shared_with_client",
        is_resolved_by_short_description: "Mortgage retention shared with client",
        create: {
            create_condition_description: "Mortgage cases where the lender has a retention amount",
            predicate: (c) =>
                mortgageInvolved(c)
                && c.case.mortgage_offer_retention_amount_pence !== null
                && c.case.mortgage_offer_retention_amount_pence > 0,
        },
        resolve: {
            is_resolved_by_long_description: `Marking the mortgage offer retention as explained to the client on the case.`,
            predicate: (c) => c.case.mortgage_offer_retention_explained_to_client,
            evidence: (c) => {
                const retention = c.case.mortgage_offer_retention_amount_pence;
                return [
                    ...(retention !== null
                        ? [neutralFact("Retention amount", formatCurrencyFromPence(retention), mortgageOfferLink)]
                        : []),
                    fact(
                        "Retention explained to client",
                        c.case.mortgage_offer_retention_explained_to_client ? "Yes" : "No",
                        c.case.mortgage_offer_retention_explained_to_client,
                        mortgageOfferLink,
                    ),
                ];
            },
        },
    },
    {
        type: "early_repayment_charge_obtained",
        is_resolved_by_short_description: "Early repayment charge answered for every charge",
        create: {
            create_condition_description: "Cases with a charge",
            predicate: (c) => c.cases_charges.length > 0,
        },
        resolve: {
            is_resolved_by_long_description: `Answering "has_early_repayment_charge" for every charge on the case.`,
            predicate: (c) =>
                c.cases_charges.length > 0
                && c.cases_charges.every((ch) => ch.has_early_repayment_charge !== null),
            evidence: (c) => c.cases_charges.map((ch, index) => {
                const label = ch.account_number.trim() !== "" ? `Charge ${ch.account_number}` : `Charge ${index + 1}`;
                return fact(
                    label,
                    ch.has_early_repayment_charge === null
                        ? "Early repayment charge not answered"
                        : ch.has_early_repayment_charge ? "Has early repayment charge" : "No early repayment charge",
                    ch.has_early_repayment_charge !== null,
                    chargesLink,
                );
            }),
        },
    },
    {
        type: "client_notified_of_early_repayment_charge",
        is_resolved_by_short_description: "Client notified of early repayment charge",
        create: {
            create_condition_description: "Cases with a charge where has_early_repayment_charge is true",
            predicate: (c) => c.cases_charges.some((ch) => ch.has_early_repayment_charge === true),
        },
        resolve: {
            is_resolved_by_long_description: `Marking the client as notified for every charge with an early repayment charge.`,
            predicate: (c) =>
                c.cases_charges
                    .filter((ch) => ch.has_early_repayment_charge === true)
                    .every((ch) => ch.client_notified_of_early_repayment_charge === true),
            evidence: (c) => c.cases_charges
                .filter((ch) => ch.has_early_repayment_charge === true)
                .map((ch, index) => {
                    const label = ch.account_number.trim() !== "" ? `Charge ${ch.account_number}` : `Charge ${index + 1}`;
                    return fact(
                        label,
                        ch.client_notified_of_early_repayment_charge ? "Client notified" : "Client not notified",
                        ch.client_notified_of_early_repayment_charge,
                        chargesLink,
                    );
                }),
        },
    },

    {
        type: "bank_accounts_verified_or_marked_unused",
        is_resolved_by_short_description: "Bank accounts verified (or marked won't be used)",
        create: { create_condition_description: "All cases", predicate: () => true },
        resolve: {
            is_resolved_by_long_description: `Every bank account on the case being either approved with all clients agreeing, or marked as not-verified-as-will-not-be-used.`,
            predicate: (c) => c.bank_accounts.length === 0 || allBankAccountsCleared(c),
            evidence: (c) => {
                if (c.bank_accounts.length === 0) {
                    return [neutralFact("Bank accounts", "None on case", bankAccountsLink)];
                }
                return c.bank_accounts.map((b, index) => {
                    const cleared =
                        b.verification_status === "not_verified_as_will_not_be_used"
                        || (b.verification_status === "approved" && b.all_clients_agree_with_this_bank_account !== null);
                    const value =
                        b.verification_status === "not_verified_as_will_not_be_used"
                            ? "Marked won't be used"
                            : b.verification_status === "approved"
                            ? b.all_clients_agree_with_this_bank_account !== null
                                ? "Approved, clients agree"
                                : "Approved, awaiting client agreement"
                            : "Not verified";
                    return fact(`Bank account ${index + 1}`, value, cleared, bankAccountsLink);
                });
            },
        },
    },
    {
        type: "charges_have_account_numbers",
        is_resolved_by_short_description: "All charges have account numbers (transfer/remortgage)",
        create: {
            create_condition_description: "Transfer or remortgage cases with at least one charge",
            predicate: (c) =>
                (c.case.transaction_type === "transfer" || c.case.transaction_type === "remortgage")
                && c.cases_charges.length > 0,
        },
        resolve: {
            is_resolved_by_long_description: `Every charge on the case having a non-empty account number.`,
            predicate: (c) =>
                c.cases_charges.length > 0
                && c.cases_charges.every((ch) => ch.account_number.trim() !== ""),
            evidence: (c) => c.cases_charges.map((ch, index) => {
                const hasNumber = ch.account_number.trim() !== "";
                return fact(
                    `Charge ${index + 1}`,
                    hasNumber ? ch.account_number : "No account number",
                    hasNumber,
                    chargesLink,
                );
            }),
        },
    },

    {
        type: "sdlt_return_uploaded_or_compass_referral_set",
        is_resolved_by_short_description: "SDLT return uploaded or Compass referral set",
        create: {
            create_condition_description: "Purchase/transfer cases with price >= £40,000",
            predicate: (c) => sdltApplies(c),
        },
        resolve: {
            is_resolved_by_long_description: `Either a draft/signed SDLT return being uploaded, or marking the case as referred to Compass, or marking that no return is required at all.`,
            predicate: (c) =>
                sdltNotRequired(c)
                || c.case.sdlt_answers_reviewed_outcome === "referred_to_compass"
                || (
                    c.case.sdlt_answers_reviewed_outcome === "compass_referral_not_required"
                    && anyReceivedDocumentExists(c, ["draft_sdlt_return", "signed_sdlt_return"])
                ),
            evidence: (c) => {
                if (sdltNotRequired(c)) {
                    return [fact("SDLT", "No return required", true, sdltLink)];
                }
                if (c.case.sdlt_answers_reviewed_outcome === "referred_to_compass") {
                    return [fact("SDLT", "Referred to Compass", true, sdltLink)];
                }
                if (c.case.sdlt_answers_reviewed_outcome === "compass_referral_not_required") {
                    const returnReceived = anyReceivedDocumentExists(c, ["draft_sdlt_return", "signed_sdlt_return"]);
                    return [
                        neutralFact("SDLT", "Compass referral not required", sdltLink),
                        fact("SDLT return", returnReceived ? "Uploaded" : "Not uploaded", returnReceived, sdltLink),
                    ];
                }
                return [fact("SDLT", "Compass referral not answered", false, sdltLink)];
            },
        },
    },
    {
        type: "sdlt_declaration_signed_by_all_clients",
        is_resolved_by_short_description: "SDLT declaration signed by every client",
        create: {
            create_condition_description: "Purchase/transfer cases with price >= £40,000",
            predicate: (c) => sdltApplies(c),
        },
        resolve: {
            is_resolved_by_long_description: `Every client on the case having signed the SDLT declaration, or marking that no return is required at all.`,
            predicate: (c) => sdltNotRequired(c) || allClientsHaveSdltDeclared(c),
            evidence: (c) => {
                if (sdltNotRequired(c)) {
                    return [fact("SDLT declaration", "Not required (no return)", true, sdltLink)];
                }
                const declaredIds = c.sdlt_declarations.map((d) => d.user_id);
                return clientsOfTypes(c.clients, ["client"]).map((client) => {
                    const signed = declaredIds.includes(client.id);
                    return fact(clientName(client), signed ? "Signed" : "Not signed", signed, sdltLink);
                });
            },
        },
    },
    {
        type: "sdlt_added_to_ledger",
        is_resolved_by_short_description: "SDLT added to ledger",
        create: {
            create_condition_description: "Purchase/transfer cases where SDLT applies (>= £40,000)",
            predicate: (c) => sdltApplies(c),
        },
        resolve: {
            is_resolved_by_long_description: `Adding a stamp_duty_land_tax ledger entry on the case, or marking that no return is required at all.`,
            predicate: (c) => sdltNotRequired(c) || anyActiveLedgerEntryOfType(c, "stamp_duty_land_tax"),
            evidence: (c) =>
                sdltNotRequired(c)
                    ? [fact("SDLT", "Nothing to pay", true, sdltLink)]
                    : [ledgerEntryPaidFact(c, "stamp_duty_land_tax", "SDLT ledger entry")],
        },
    },

    {
        type: "all_clients_added_and_right_to_buy_or_sell_checked",
        is_resolved_by_short_description: "All clients added and right to buy/sell checked",
        create: {
            create_condition_description: "All cases",
            predicate: () => true,
        },
        resolve: {
            is_resolved_by_long_description: `Adding all clients, and setting the right to buy/sell field`,
            predicate: (c) => c.case.all_clients_added_and_right_to_buy_or_sell_checked,
            evidence: (c) => [
                fact(
                    "Right to buy/sell checked",
                    c.case.all_clients_added_and_right_to_buy_or_sell_checked ? "Checked" : "Not checked",
                    c.case.all_clients_added_and_right_to_buy_or_sell_checked,
                    clientsLink,
                ),
                ...c.clients.map((client) => neutralFact(clientName(client), UserCaseUserType_UserCaseUserTypeDisplayName(client.user_type), clientsLink)),
            ],
        },
    },
    {
        type: "joint_ownership_held_as_answered",
        is_resolved_by_short_description: "Joint ownership held as answered",
        create: {
            create_condition_description: "Purchase/transfer/remortgage with more than one client",
            predicate: (c) =>
                (c.case.transaction_type === "purchase" || c.case.transaction_type === "transfer" || c.case.transaction_type === "remortgage")
                && c.clients.filter((u) => u.user_type === "client").length > 1,
        },
        resolve: {
            is_resolved_by_long_description: `Setting how joint ownership is held on the case (anything other than "unknown").`,
            predicate: (c) => c.case.joint_ownership_held_as !== "unknown",
            evidence: (c) => [
                fact(
                    "Joint ownership held as",
                    c.case.joint_ownership_held_as === "unknown" ? "Not set" : c.case.joint_ownership_held_as,
                    c.case.joint_ownership_held_as !== "unknown",
                ),
            ],
        },
    },

    {
        type: "apportionment_question_answered",
        is_resolved_by_short_description: "Apportionment question answered",
        create: {
            create_condition_description: "Cases with a management company",
            predicate: (c) => c.case.has_management_company === true,
        },
        resolve: {
            is_resolved_by_long_description: `Answering the apportionment question on every linked management company. Stays open while any one is unanswered.`,
            predicate: (c) =>
                c.management_companies.length > 0
                && c.management_companies.every((m) => m.requires_apportionment_statement !== null),
            evidence: (c) =>
                c.management_companies.length === 0
                    ? [fact("Management companies", "None linked", false, managementCompanyLink)]
                    : c.management_companies.map((m) =>
                        fact(
                            m.company_name.trim() !== "" ? m.company_name : "Management company",
                            m.requires_apportionment_statement === null
                                ? "Not answered"
                                : m.requires_apportionment_statement ? "Required" : "Not required",
                            m.requires_apportionment_statement !== null,
                            managementCompanyLink,
                        )
                    ),
        },
    },

    {
        type: "dual_rep_lender_conflict_approved_if_required",
        is_resolved_by_short_description: "Dual rep lender conflict approved (if required)",
        create: {
            create_condition_description: "Dual-rep mortgage cases where the lender doesn't allow dual rep",
            predicate: (c) =>
                c.dual_rep.case_is_dual_rep
                && c.case.purchase_payment_method === "mortgage"
                && CaseLenderDictionary[c.case.mortgage_lender_name].allowsDualRep === false,
        },
        resolve: {
            is_resolved_by_long_description: `Marking the dual-rep lender conflict as approved-to-continue on the case.`,
            predicate: (c) => c.case.dual_rep_lender_conflict_approved_to_continue !== null,
            evidence: (c) => {
                const approved = c.case.dual_rep_lender_conflict_approved_to_continue;
                return [
                    fact(
                        "Dual-rep lender conflict",
                        approved !== null ? `Approved to continue ${formatEvidenceDate(approved)}` : "Not approved",
                        approved !== null,
                    ),
                ];
            },
        },
    },
    {
        type: "dual_rep_missing_consent_obtained",
        is_resolved_by_short_description: "Dual rep consent obtained from all our-side clients",
        create: {
            create_condition_description: "Dual-rep cases",
            predicate: (c) => c.dual_rep.case_is_dual_rep,
        },
        resolve: {
            is_resolved_by_long_description: `Every our-side client on the case consenting to dual representation.`,
            predicate: (c) => c.all_our_side_clients_consent_to_dual_rep,
            evidence: (c) => [
                fact(
                    "Our-side clients consent to dual rep",
                    c.all_our_side_clients_consent_to_dual_rep ? "All consented" : "Not all consented",
                    c.all_our_side_clients_consent_to_dual_rep,
                    dualRepLink,
                ),
            ],
        },
    },

    {
        type: "leasehold_fee_added_to_ledger",
        is_resolved_by_short_description: "Leasehold fee added to ledger",
        create: {
            create_condition_description: "Cases with a leasehold property",
            predicate: (c) => anyLeaseholdProperty(c),
        },
        resolve: {
            is_resolved_by_long_description: `Adding a leasehold_fee ledger entry on the case.`,
            predicate: (c) => anyActiveLedgerEntryOfType(c, "leasehold_fee"),
            evidence: (c) => [ledgerEntryPaidFact(c, "leasehold_fee", "Leasehold fee")],
        },
    },
    {
        type: "land_registry_fee_added_to_ledger",
        is_resolved_by_short_description: "Land Registry fee added to ledger",
        create: {
            create_condition_description: "Purchase/transfer/remortgage cases",
            predicate: (c) =>
                c.case.transaction_type === "purchase"
                || c.case.transaction_type === "transfer"
                || c.case.transaction_type === "remortgage",
        },
        resolve: {
            is_resolved_by_long_description: `Adding a land_reg_fee ledger entry on the case.`,
            predicate: (c) => anyActiveLedgerEntryOfType(c, "land_reg_fee"),
            evidence: (c) => [ledgerEntryPaidFact(c, "land_reg_fee", "Land Registry fee")],
        },
    },
    {
        type: "electronic_transfer_fee_added_to_ledger",
        is_resolved_by_short_description: "Electronic transfer fee added to ledger",
        create: {
            create_condition_description: "Purchase/sale always; remortgage if a mortgage_redemption ledger entry exists",
            predicate: (c) =>
                c.case.transaction_type === "purchase"
                || c.case.transaction_type === "sale"
                || (c.case.transaction_type === "remortgage" && anyActiveLedgerEntryOfType(c, "mortgage_redemption")),
        },
        resolve: {
            is_resolved_by_long_description: `Adding an electronic_transfer ledger entry on the case.`,
            predicate: (c) => anyActiveLedgerEntryOfType(c, "electronic_transfer"),
            evidence: (c) => [ledgerEntryPaidFact(c, "electronic_transfer", "Electronic transfer fee")],
        },
    },
    {
        type: "estate_agent_fee_added_to_ledger",
        is_resolved_by_short_description: "Estate agent fee added to ledger",
        create: {
            create_condition_description: "Sale cases that DO have an estate agent",
            predicate: (c) => c.case.transaction_type === "sale" && c.case.no_estate_agent === false,
        },
        resolve: {
            is_resolved_by_long_description: `Adding an estate_agent_fee ledger entry on the case.`,
            predicate: (c) => anyActiveLedgerEntryOfType(c, "estate_agent_fee"),
            evidence: (c) => [ledgerEntryPaidFact(c, "estate_agent_fee", "Estate agent fee")],
        },
    },
    {
        type: "deposit_added_to_ledger",
        is_resolved_by_short_description: "Deposit added to ledger",
        create: {
            create_condition_description: "Purchase cases with price > 0 and no related transaction (transfers/remortgages don't take a deposit)",
            predicate: (c) =>
                c.case.transaction_type === "purchase"
                && c.case.price_pence !== null
                && c.case.price_pence > 0
                && c.has_related_transaction === false,
        },
        resolve: {
            is_resolved_by_long_description: `Adding a money_from_client_deposit ledger entry on the case.`,
            predicate: (c) => anyActiveLedgerEntryOfType(c, "money_from_client_deposit"),
            evidence: (c) => [ledgerEntryPaidFact(c, "money_from_client_deposit", "Deposit")],
        },
    },
    {
        type: "draft_completion_statement_uploaded",
        is_resolved_by_short_description: "Draft completion statement uploaded",
        create: { create_condition_description: "All cases", predicate: () => true },
        resolve: {
            is_resolved_by_long_description: `A completion_statement_draft document being uploaded and marked as received.`,
            predicate: (c) => anyReceivedDocumentExists(c, ["completion_statement_draft"]),
            evidence: (c) => [receivedDocFact(c, ["completion_statement_draft"], "Draft completion statement")],
        },
    },

    {
        type: "divorce_document_type_selected",
        is_resolved_by_short_description: "Divorce document type selected",
        create: {
            create_condition_description: "Divorce cases on transfer, remortgage, or purchase",
            predicate: (c) =>
                c.case.seller_reason_for_sale === "divorce"
                && (
                    c.case.transaction_type === "transfer"
                    || c.case.transaction_type === "remortgage"
                    || c.case.transaction_type === "purchase"
                ),
        },
        resolve: {
            is_resolved_by_long_description: `The operator picking which divorce document the seller will provide (separation agreement, consent order, final order, court petition, or statement of truth) on the contract details block.`,
            predicate: (c) => c.case.divorce_document_type !== "unknown",
            evidence: (c) => [
                fact(
                    "Divorce document type",
                    c.case.divorce_document_type === "unknown" ? "Not selected" : c.case.divorce_document_type,
                    c.case.divorce_document_type !== "unknown",
                ),
            ],
        },
    },

    {
        type: "signed_mortgage_deed_received",
        is_resolved_by_short_description: "Signed mortgage deed received",
        create: {
            create_condition_description: "Mortgage-involved cases (purchase/transfer with mortgage, or remortgage) where a signed_mortgage_deed slot exists, unless the lender has confirmed no new mortgage deed is required",
            predicate: (c) =>
                mortgageInvolved(c)
                && c.case.mortgage_offer_lender_requires_a_new_mortgage_deed !== false
                && anyDocSlotExists(c, ["signed_mortgage_deed"]),
        },
        resolve: {
            is_resolved_by_long_description: `Every signed_mortgage_deed document slot on the case being uploaded and marked as received.`,
            predicate: (c) => allDocumentSlotsReceived(c, ["signed_mortgage_deed"]),
            evidence: (c) => [docSlotsReceivedFact(c, ["signed_mortgage_deed"], "Signed mortgage deeds")],
        },
    },
    {
        type: "signed_occupier_consent_received",
        is_resolved_by_short_description: "Signed occupier consent received",
        create: {
            create_condition_description: "Cases marked as having other occupiers where a signed_occupiers_consent_form slot exists",
            predicate: (c) =>
                c.case.property_has_other_occupiers === true
                && anyDocSlotExists(c, ["signed_occupiers_consent_form"]),
        },
        resolve: {
            is_resolved_by_long_description: `Every signed_occupiers_consent_form document slot on the case being uploaded and marked as received.`,
            predicate: (c) => allDocumentSlotsReceived(c, ["signed_occupiers_consent_form"]),
            evidence: (c) => [docSlotsReceivedFact(c, ["signed_occupiers_consent_form"], "Signed occupier consent forms")],
        },
    },
];

export const sourceOfWealthSatisfiedItem = (
    gateOnNotSignedOffForExchange: boolean,
): TRoadblockCheckListItem => ({
    type: "source_of_wealth_satisfied",
    is_resolved_by_short_description: "Source of wealth satisfied",
    create: {
        create_condition_description: gateOnNotSignedOffForExchange
            ? "Cases with at least one funder flagged as needing a source of wealth check, until exchange is signed off"
            : "Cases with at least one funder flagged as needing a source of wealth check",
        predicate: (c) =>
            fundersNeedingSourceOfWealth(c).length > 0
            && (
                gateOnNotSignedOffForExchange === false
                || c.case.is_signed_off_by_authoriser_as_ready_for_exchange === null
            ),
    },
    resolve: {
        is_resolved_by_long_description: `A conveyancer marking source of wealth as passed for every funder flagged as needing a source of wealth check. A source of wealth report on file is evidence — it does not resolve this item on its own; a staff member must confirm they are satisfied.`,
        predicate: (c) =>
            fundersNeedingSourceOfWealth(c).every((client) => client.source_of_wealth_passed_at !== null),
        evidence: (c) =>
            fundersNeedingSourceOfWealth(c).map((client) =>
                fact(
                    clientName(client),
                    client.source_of_wealth_passed_at !== null
                        ? `Passed ${formatEvidenceDate(client.source_of_wealth_passed_at)}`
                        : "Not yet satisfied",
                    client.source_of_wealth_passed_at !== null,
                    clientsLink,
                )
            ),
    },
});

export const allTeamItemsResolved = (
    c: TRoadblockCaseData,
    otherItems: Array<TRoadblockCheckListItem>,
): boolean =>
    otherItems
        .filter((i) => i.type !== "clients_authorised_exchange_and_complete")
        .filter((i) =>
            checklistItemResolvedByParty[i.type] === "conveyancer"
            || checklistItemResolvedByParty[i.type] === "third_party"
        )
        .every((i) => i.create.predicate(c) === false || i.resolve.predicate(c) === true);

export const clientsAuthorisedExchangeAndCompleteItem = (
    otherItems: Array<TRoadblockCheckListItem>,
    overrides?: { shortDescription?: string; longDescription?: string },
): TRoadblockCheckListItem => ({
    type: "clients_authorised_exchange_and_complete",
    is_resolved_by_short_description: overrides?.shortDescription ?? "Clients authorised exchange and complete",
    create: {
        create_condition_description: "Only once every team/chased item on this roadblock that applies to the case is resolved (so we don't ask the client to authorise before the prep is in)",
        predicate: (c) => allTeamItemsResolved(c, otherItems),
    },
    resolve: {
        is_resolved_by_long_description: overrides?.longDescription ?? `Recording the clients' authorisation to exchange and complete on the case.`,
        predicate: (c) => c.case.clients_authorise_exchange_and_complete !== null,
        evidence: (c) => {
            const authorised = c.case.clients_authorise_exchange_and_complete;
            return [
                fact(
                    "Clients' authorisation",
                    authorised !== null ? `Authorised ${formatEvidenceDate(authorised)}` : "Not authorised",
                    authorised !== null,
                ),
            ];
        },
    },
});
